import {
  formatPointsNumber,
  type IRedemption,
} from "@ticketon/rewards-core";
import { useRewardRedemption } from "@ticketon/rewards-graphql";
import {
  Separator,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "ui";
import type { ReactNode } from "react";
import type { Translator } from "../types/nav";
import { useRewardsAdmin } from "../host/RewardsAdminHost";
import { enumValueKey } from "../util/options";
import { StatusBadge } from "../shared/StatusBadge";
import { LoadingState, ErrorState, EmptyState } from "../shared/states";

export interface IRedemptionDetailDrawerProps {
  redemptionId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  t?: Translator;
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="text-body-sm text-gray-500">{label}</span>
      <span className="text-right text-body-sm font-medium text-ticketon-dark-gray">{value}</span>
    </div>
  );
}

/** Slide-out detail panel for a single redemption. */
export function RedemptionDetailDrawer({
  redemptionId,
  open,
  onOpenChange,
  t: tOverride,
}: IRedemptionDetailDrawerProps) {
  const { t } = useRewardsAdmin({ t: tOverride });
  const { data, isLoading, error, refetch } = useRewardRedemption(redemptionId ?? undefined);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full max-w-md overflow-y-auto" data-testid="redemption-drawer">
        <SheetHeader>
          <SheetTitle>{t("rewards.redemption.detail_title")}</SheetTitle>
          <SheetDescription>{t("rewards.redemption.detail_subtitle")}</SheetDescription>
        </SheetHeader>

        {isLoading && <LoadingState t={t} />}
        {error && <ErrorState error={error} onRetry={refetch} t={t} />}
        {!isLoading && !error && !data && <EmptyState t={t} title={t("rewards.redemption.not_found")} />}

        {data && <RedemptionBody redemption={data} t={t} />}
      </SheetContent>
    </Sheet>
  );
}

function RedemptionBody({ redemption, t }: { redemption: IRedemption; t: Translator }) {
  const { formatDate, formatDateTime } = useRewardsAdmin();
  return (
    <div className="mt-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <StatusBadge status={redemption.status} t={t} />
        <StatusBadge status={redemption.fulfillmentStatus} t={t} />
      </div>
      <Separator className="my-2" />
      <Row label={t("rewards.redemption.item")} value={redemption.item.name} />
      <Row
        label={t("rewards.redemption.reward_type")}
        value={t(enumValueKey("rewards.reward_type", redemption.item.rewardType))}
      />
      <Row
        label={t("rewards.redemption.points_spent")}
        value={formatPointsNumber(redemption.pointsSpent)}
      />
      {redemption.redemptionCode && (
        <Row
          label={t("rewards.redemption.code")}
          value={<span className="font-mono">{redemption.redemptionCode}</span>}
        />
      )}
      <Row label={t("rewards.redemption.created_at")} value={formatDateTime(redemption.createdAt)} />
      {redemption.fulfilledAt && (
        <Row
          label={t("rewards.redemption.fulfilled_at")}
          value={formatDateTime(redemption.fulfilledAt)}
        />
      )}
      {redemption.expiresAt && (
        <Row label={t("rewards.redemption.expires_at")} value={formatDate(redemption.expiresAt)} />
      )}
      {redemption.purchaseReference && (
        <Row label={t("rewards.redemption.purchase_reference")} value={redemption.purchaseReference} />
      )}
      {redemption.cancellationReason && (
        <Row
          label={t("rewards.redemption.cancellation_reason")}
          value={redemption.cancellationReason}
        />
      )}
    </div>
  );
}
